import { Outlet } from 'react-router';

import { ID_PARAMS, NESTED_ROUTES } from 'common/src/routes/BaseRoutes';

import { API_BASE_PATHS } from 'portal/src/api/constants';
import Accounts from 'portal/src/components/administration/Accounts/Accounts';
import Bills from 'portal/src/components/administration/Bills/Bills';
import Budgets from 'portal/src/components/administration/Budgets';
import EnergyAssets from 'portal/src/components/administration/EnergyAssets';
import Facilities from 'portal/src/components/administration/Facilities';
import FacilityGroups from 'portal/src/components/administration/FacilityGroups';
import AccountForm from 'portal/src/components/administration/Form/Accounts/AccountForm';
import BillForm from 'portal/src/components/administration/Form/Bills/BillForm';
import { BudgetForm } from 'portal/src/components/administration/Form/Budgets/BudgetForm';
import EnergyAssetForm from 'portal/src/components/administration/Form/EnergyAssets/EnergyAssetForm';
import FacilityForm from 'portal/src/components/administration/Form/Facilities/FacilityForm';
import FacilityGroupForm from 'portal/src/components/administration/Form/FacilityGroups/FacilityGroupForm';
import MeterForm from 'portal/src/components/administration/Form/Meters/MeterForm';
import ProjectForm from 'portal/src/components/administration/Form/Projects/ProjectForm';
import UtilityForm from 'portal/src/components/administration/Form/Utilities/UtilityForm';
import WorkOrderForm from 'portal/src/components/administration/Form/WorkOrders/WorkOrderForm';
import Meters from 'portal/src/components/administration/Meters/Meters';
import Projects from 'portal/src/components/administration/Projects';
import Utilities from 'portal/src/components/administration/Utilities';
import BaseForm from 'portal/src/layouts/base/BaseForm';
import AdministrationUserForm from 'portal/src/pages/administration/AdministrationUserForm';
import {
  isOrganizationViewEnabled,
  isWorkOrderViewEnabled,
} from 'portal/src/pages/administration/adminViewEnabled';
import AllocationTemplateView from 'portal/src/pages/administration/AllocationTemplateView';
import OrganizationView from 'portal/src/pages/administration/OrganizationView';
import WorkOrdersView from 'portal/src/pages/administration/WorkOrderView';

import { RouteDefinition } from './routeGenerator';

export const administrationRouteDefinitions: RouteDefinition[] = [
  {
    children: [
      {
        element: (
          <BaseForm
            apiPath={API_BASE_PATHS.FACILITIES}
            Component={FacilityForm}
          />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <Facilities />,
        index: true,
      },
    ],
    element: <Outlet />,
    path: NESTED_ROUTES.ADMINISTRATION.FACILITIES,
    description: 'Add, edit, or delete facilities',
    name: 'Facilities',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm
            apiPath={API_BASE_PATHS.FACILITY_GROUPS}
            Component={FacilityGroupForm}
          />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <FacilityGroups />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.FACILITY_GROUPS,
    description: 'Add, edit, or delete facility groups',
    name: 'Facility Groups',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm
            apiPath={API_BASE_PATHS.UTILITIES}
            Component={UtilityForm}
          />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <Utilities />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.UTILITIES,
    description: 'Add, edit, or delete utilities',
    name: 'Utilities',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: <Outlet />,
        children: [
          {
            element: (
              <BaseForm
                apiPath={API_BASE_PATHS.ACCOUNTS}
                Component={AccountForm}
              />
            ),
            index: true,
          },
          {
            element: <AllocationTemplateView />,
            path: NESTED_ROUTES.ADMINISTRATION.ALLOCATION_TEMPLATE,
          },
        ],
        path: ID_PARAMS.ANY,
      },
      {
        element: <Accounts />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.ACCOUNTS,
    description: 'Add, edit, or delete accounts',
    name: 'Accounts',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm apiPath={API_BASE_PATHS.METERS} Component={MeterForm} />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <Meters />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.METERS,
    description: 'Add, edit, or delete meters',
    name: 'Meters',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm apiPath={API_BASE_PATHS.BILLS} Component={BillForm} />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <Bills />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.BILLS,
    description: 'Add, edit, or delete bills',
    name: 'Bills',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm
            apiPath={API_BASE_PATHS.ENERGY_ASSETS}
            Component={EnergyAssetForm}
          />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <EnergyAssets />,
        index: true,
      },
    ],
    dividerBefore: true,
    path: NESTED_ROUTES.ADMINISTRATION.ENERGY_ASSETS,
    description: 'Add, edit, or delete energy assets',
    name: 'Energy Assets',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm apiPath={API_BASE_PATHS.BUDGETS} Component={BudgetForm} />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <Budgets />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.BUDGETS,
    description: 'Add, edit, copy or delete budgets',
    name: 'Budgets',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm apiPath={API_BASE_PATHS.PROJECTS} Component={ProjectForm} />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <Projects />,
        index: true,
      },
    ],
    path: NESTED_ROUTES.ADMINISTRATION.PROJECTS,
    description: 'Add, edit, or delete projects',
    name: 'Projects',
  },
  {
    element: <Outlet />,
    children: [
      {
        element: (
          <BaseForm
            apiPath={API_BASE_PATHS.WORK_ORDERS}
            Component={WorkOrderForm}
          />
        ),
        path: ID_PARAMS.ANY,
      },
      {
        element: <WorkOrdersView />,
        index: true,
      },
    ],
    shouldRenderLink: isWorkOrderViewEnabled,
    path: NESTED_ROUTES.ADMINISTRATION.WORK_ORDERS,
    description: 'Add, edit, or delete work orders',
    name: 'Work Orders',
  },
  {
    children: [
      {
        element: <OrganizationView />,
        index: true,
      },
      {
        element: (
          <BaseForm
            apiPath={API_BASE_PATHS.USERS}
            Component={AdministrationUserForm}
          />
        ),
        path: NESTED_ROUTES.ADMINISTRATION.ORG_USERS,
      },
    ],
    element: <Outlet />,
    shouldRenderLink: isOrganizationViewEnabled,
    dividerBefore: true,
    path: NESTED_ROUTES.ADMINISTRATION.ORGANIZATION,
    description: 'Edit organization',
    name: 'Settings',
  },
];
